import { Fragment, useCallback, useEffect, useState } from 'react'
import { Check, Copy, Loader2, MoreVertical, Plus, RefreshCw, TicketPercent } from 'lucide-react'
import Layout from '../../components/Layout'
import DiscountFormModal from '../../components/admin/DiscountFormModal'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Card, CardContent } from '../../components/ui/card'
import { ConfirmDialog } from '../../components/ui/confirm-dialog'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu'
import { useNotification } from '../../contexts/NotificationContext'
import {
  activateDiscount, deactivateDiscount, deleteDiscount, discountShareLink, fetchDiscounts,
} from '../../services/discountService'

const STATUS_BADGE = { ACTIVE: 'teal', SCHEDULED: 'blue', EXPIRED: 'muted' }

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })

function metaLine(d) {
  const parts = []
  if (d.endsAt) parts.push(`${d.status === 'EXPIRED' ? 'Expired' : 'Expires'} ${fmtDate(d.endsAt)}`)
  else parts.push('No expiration')
  parts.push(`Used ${d.usedCount}${d.usageLimit ? ` of ${d.usageLimit}` : '×'}`)
  if (d.oncePerCustomer) parts.push('1 per customer')
  return parts.join(' · ')
}

function DiscountCard({ discount: d, onChanged }) {
  const notify = useNotification()
  const [copied, setCopied] = useState(false)
  const [busy, setBusy] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(discountShareLink(d.code))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      notify.error('Could not copy — long-press the link to copy manually')
    }
  }

  const run = async (fn, doneMsg) => {
    setBusy(true)
    try {
      await fn(d.id)
      notify.success(doneMsg)
      onChanged()
    } catch (err) {
      notify.error(err.message || 'Action failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-base font-semibold break-all">{d.code}</span>
              <Badge variant={STATUS_BADGE[d.status] || 'muted'}>{d.status.toLowerCase()}</Badge>
            </div>
            {d.summary && <p className="mt-1 text-sm text-muted-foreground">{d.summary}</p>}
            <p className="mt-1 text-xs text-muted-foreground">{metaLine(d)}</p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="shrink-0" disabled={busy} aria-label={`Actions for ${d.code}`}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreVertical className="h-4 w-4" />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {d.status === 'ACTIVE' ? (
                <DropdownMenuItem onClick={() => run(deactivateDiscount, `${d.code} deactivated`)}>
                  Deactivate
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={() => run(activateDiscount, `${d.code} activated`)}>
                  Activate
                </DropdownMenuItem>
              )}
              <DropdownMenuItem className="text-destructive" onClick={() => setConfirmDelete(true)}>
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="mt-3 flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={copyLink}
            className="w-full sm:w-auto"
            aria-label={`Copy share link for ${d.code}`}
          >
            {copied ? <Check className="h-4 w-4 mr-2 text-[var(--teal-11)]" /> : <Copy className="h-4 w-4 mr-2" />}
            {copied ? 'Copied!' : 'Copy share link'}
          </Button>
          <span className="hidden truncate text-xs text-muted-foreground sm:block">
            getdub.com/discount/{d.code}
          </span>
        </div>
      </CardContent>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={`Delete ${d.code}?`}
        description="The code stops working immediately and disappears from this list. Past orders are unaffected."
        confirmText="Delete code"
        variant="destructive"
        onConfirm={() => run(deleteDiscount, `${d.code} deleted`)}
      />
    </Card>
  )
}

// Shown when the edge function reports the Shopify Admin token secret is
// missing — one-time setup, see the runbook note it renders.
function SetupNotice() {
  return (
    <Card>
      <CardContent className="p-5 text-sm space-y-2">
        <p className="font-semibold">One-time setup needed</p>
        <p className="text-muted-foreground">
          Shopify API access isn&apos;t configured yet. Create an app in the Shopify Dev
          Dashboard with the
          <span className="font-mono"> read_discounts</span> and
          <span className="font-mono"> write_discounts</span> scopes, install it on the store,
          then add its client credentials as the
          <span className="font-mono"> SHOPIFY_CLIENT_ID</span> and
          <span className="font-mono"> SHOPIFY_CLIENT_SECRET</span> secrets on the
          <span className="font-mono"> shopify-discounts</span> Supabase edge function.
        </p>
      </CardContent>
    </Card>
  )
}

export default function DiscountManagement({ embedded = false }) {
  const Wrapper = embedded ? Fragment : Layout
  const notify = useNotification()
  const [discounts, setDiscounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [setupRequired, setSetupRequired] = useState(false)
  const [showForm, setShowForm] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchDiscounts()
      if (data.setupRequired) {
        setSetupRequired(true)
      } else {
        setSetupRequired(false)
        setDiscounts(data.discounts || [])
      }
    } catch (err) {
      notify.error(err.message || 'Failed to load discount codes')
    } finally {
      setLoading(false)
    }
  }, [notify])

  useEffect(() => { load() }, [load])

  return (
    <Wrapper>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <TicketPercent className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-bold">Discount Codes</h2>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={load} disabled={loading} aria-label="Refresh">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            {!setupRequired && (
              <Button onClick={() => setShowForm(true)}>
                <Plus className="h-4 w-4 mr-1.5" />
                New code
              </Button>
            )}
          </div>
        </div>

        <p className="mb-4 text-sm text-muted-foreground">
          Share links open the merch store with the code applied at checkout automatically.
        </p>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : setupRequired ? (
          <SetupNotice />
        ) : discounts.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-sm text-muted-foreground">
              No discount codes yet — create the first one.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {discounts.map((d) => (
              <DiscountCard key={d.id} discount={d} onChanged={load} />
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <DiscountFormModal onClose={() => setShowForm(false)} onCreated={load} />
      )}
    </Wrapper>
  )
}
